import { makeAutoObservable, runInAction } from "mobx";
import { formatApiDateTime } from "../../utils/Backend";
import { uploadAppFileWithKey } from "../../global/OssUploadApi";
import { compressFileToJpegFile } from "../../utils/imageCompressToJpeg";
import { globalModel } from "../../global/GlobalModel";
import {
  buildCsMessageCacheScope,
  loadCsMessageCache,
  mergeCsMessages,
  resolveCsMaxMessageId,
  saveCsMessageCache,
  trimCsMessagesForDisplay,
  type CsMessageCacheScope,
} from "./CsMessageLocalStore";
import { SpaceCustomerChatApi } from "./SpaceCustomerChatApi";
import type { CsMessage, CsSyncReadedAndGetUnreadResult } from "./SpaceCustomerServiceTypes";
import {
  CS_MAX_VISIBLE_MESSAGES,
  CS_WELCOME_MESSAGE_ID,
  CS_WELCOME_TEXT,
} from "./SpaceCustomerServiceTypes";

function cmpMsgId(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

function trimToVisibleWindow(messages: CsMessage[]): CsMessage[] {
  if (messages.length <= CS_MAX_VISIBLE_MESSAGES) return messages;
  return messages.slice(messages.length - CS_MAX_VISIBLE_MESSAGES);
}

function stripWelcome(messages: CsMessage[]): CsMessage[] {
  return messages.filter((m) => m.id !== CS_WELCOME_MESSAGE_ID);
}

function formatLocalSentAt(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

function createWelcomeMessage(): CsMessage {
  return {
    id: CS_WELCOME_MESSAGE_ID,
    senderType: "STAFF",
    content: CS_WELCOME_TEXT,
    messageType: "SYSTEM_HINT",
    sentAt: formatLocalSentAt(),
  };
}

function withWelcomeIfEmpty(messages: CsMessage[]): CsMessage[] {
  return messages.length > 0 ? messages : [createWelcomeMessage()];
}

function resolveMaxMessageId(messages: CsMessage[]): string | null {
  return resolveCsMaxMessageId(stripWelcome(messages).filter((m) => !isLocalOutboundMessage(m)));
}

function resolveReadUpToMessageId(messages: CsMessage[]): string | null {
  return resolveMaxMessageId(messages);
}

function isLocalOutboundMessage(m: CsMessage): boolean {
  return Boolean(m.imageSendStatus) || m.id.startsWith("local-");
}

function makeLocalImageMessageId(): string {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const CS_IMAGE_COMPRESS_MAX_EDGE = 1280;
const CS_IMAGE_COMPRESS_QUALITY = 0.86;

function resolveCsImageFileExt(file: File): string {
  const ext = file.name.split(".").pop()?.trim().toLowerCase() || "jpg";
  const normalized = ext === "jpeg" ? "jpg" : ext;
  if (normalized === "png" || normalized === "webp" || normalized === "gif") {
    return normalized;
  }
  return "jpg";
}

async function prepareCsImageUploadFile(
  file: File
): Promise<{ uploadFile: File; fileExt: string; compressed: boolean; compressNote: string }> {
  try {
    const jpeg = await compressFileToJpegFile(file, CS_IMAGE_COMPRESS_MAX_EDGE, CS_IMAGE_COMPRESS_QUALITY);
    return { uploadFile: jpeg, fileExt: "jpg", compressed: true, compressNote: "压缩成功" };
  } catch (e) {
    return {
      uploadFile: file,
      fileExt: resolveCsImageFileExt(file),
      compressed: false,
      compressNote: `压缩失败: ${e instanceof Error ? e.message : String(e)}，使用原图`,
    };
  }
}

function formatUploadStepTime(): string {
  return new Date().toLocaleTimeString("zh-CN", { hour12: false });
}

const CS_IMAGE_UPLOAD_DEBUG = process.env.NODE_ENV === "development";

function mergeMessages(existing: CsMessage[], incoming: CsMessage[]): CsMessage[] {
  return mergeCsMessages(existing, incoming);
}

function applyRecalled(messages: CsMessage[], recalledIds?: string[]): CsMessage[] {
  if (!recalledIds?.length) return messages;
  const set = new Set(recalledIds);
  return messages.map((m) => (set.has(m.id) ? { ...m, recalled: true, content: "" } : m));
}

export class SpaceCustomerServiceModel {
  conversationId: string | null = null;
  messages: CsMessage[] = [];
  loading = false;
  loadingOlder = false;
  sending = false;
  hasOlder = false;
  historyRecycled = false;
  draft = "";
  error: string | null = null;
  imageSendFailureAlert: { messageId: string; log: string } | null = null;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private maxMessageId: string | null = null;
  private maxRecallEventId: string | null = null;
  private disposed = false;
  private readonly spaceId: string;
  private readonly displayName: string;
  private readonly cacheScope: CsMessageCacheScope;
  private readonly onUnreadChange?: (count: number) => void;
  private readonly imageRetryFileByMessageId = new Map<string, File>();
  private readonly imageSendDebugLogByMessageId = new Map<string, string>();

  constructor(
    spaceId: string,
    displayName = "空间客服",
    opts?: { onUnreadChange?: (count: number) => void }
  ) {
    this.spaceId = spaceId.trim();
    this.displayName = displayName;
    this.cacheScope = buildCsMessageCacheScope(this.spaceId, globalModel.currentUser?.id);
    this.onUnreadChange = opts?.onUnreadChange;
    makeAutoObservable(this);
  }

  get canSend(): boolean {
    return Boolean(this.conversationId) && !this.sending && this.draft.trim().length > 0;
  }

  setDraft(v: string) {
    this.draft = v;
  }

  async bootstrap() {
    this.disposed = false;
    this.error = null;
    this.hydrateFromLocalCache();
    try {
      const res = await SpaceCustomerChatApi.getOrCreateConversation(this.spaceId);
      if (this.disposed) return;
      if (typeof res === "string") {
        runInAction(() => {
          this.error = "加载失败";
        });
        return;
      }
      runInAction(() => {
        this.conversationId = res.conversationId;
      });
      await this.syncFromServer(true);
      if (this.disposed) return;
      this.startPoll();
    } finally {
      if (!this.disposed) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  dispose() {
    this.disposed = true;
    this.stopPoll();
    for (const m of this.messages) {
      this.revokeLocalPreview(m);
    }
  }

  private hydrateFromLocalCache() {
    const cached = loadCsMessageCache(this.cacheScope);
    if (!cached?.messages.length) {
      runInAction(() => {
        this.loading = true;
      });
      return;
    }
    const display = trimCsMessagesForDisplay(cached.messages);
    runInAction(() => {
      this.conversationId = cached.conversationId;
      this.messages = withWelcomeIfEmpty(display.map(normalizeMessage));
      this.hasOlder = Boolean(cached.hasOlder);
      this.historyRecycled = Boolean(cached.historyRecycled);
      this.maxMessageId = cached.maxMessageId ?? resolveMaxMessageId(display);
      this.loading = false;
    });
  }

  async loadOlder() {
    if (!this.conversationId || this.loadingOlder || !this.hasOlder || this.historyRecycled) return;
    const firstId = stripWelcome(this.messages)[0]?.id;
    if (!firstId) return;
    this.loadingOlder = true;
    try {
      const res = await SpaceCustomerChatApi.listMessages(this.conversationId, firstId, 50);
      if (typeof res === "string") return;
      runInAction(() => {
        const merged = trimToVisibleWindow(
          mergeMessages(res.list.map(normalizeMessage), stripWelcome(this.messages))
        );
        this.messages = withWelcomeIfEmpty(merged);
        this.hasOlder = Boolean(res.hasOlder);
        this.historyRecycled = Boolean(res.historyRecycled);
      });
      this.persistToLocal();
    } finally {
      runInAction(() => {
        this.loadingOlder = false;
      });
    }
  }

  async send() {
    const text = this.draft.trim();
    if (!this.conversationId || !text || this.sending) return;
    this.sending = true;
    try {
      const res = await SpaceCustomerChatApi.sendMessage(this.conversationId, text, "TEXT");
      if (typeof res === "string") return;
      runInAction(() => {
        this.draft = "";
        const merged = mergeMessages(stripWelcome(this.messages), [normalizeMessage(res)]);
        const trimmed = trimToVisibleWindow(merged);
        this.messages = withWelcomeIfEmpty(trimmed);
        this.maxMessageId = resolveMaxMessageId(trimmed);
      });
      this.persistToLocal();
      void this.syncFromServer(true);
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  sendImage(file: File) {
    if (!this.conversationId) return;
    const localId = makeLocalImageMessageId();
    const previewUrl = URL.createObjectURL(file);
    const optimistic: CsMessage = {
      id: localId,
      senderType: "CUSTOMER",
      content: previewUrl,
      messageType: "IMAGE",
      sentAt: formatLocalSentAt(),
      localPreviewUrl: previewUrl,
      imageSendStatus: "sending",
    };
    runInAction(() => {
      const merged = mergeMessages(stripWelcome(this.messages), [optimistic]);
      const trimmed = trimToVisibleWindow(merged);
      this.messages = withWelcomeIfEmpty(trimmed);
    });
    void this.uploadAndSendImage(localId, file);
  }

  retryFailedImage(failedMessageId: string) {
    const file = this.imageRetryFileByMessageId.get(failedMessageId);
    if (!file) return;
    const existing = this.messages.find((m) => m.id === failedMessageId);
    if (!existing || existing.imageSendStatus === "sending") return;
    runInAction(() => {
      this.imageSendFailureAlert = null;
      this.messages = this.messages.map((m) =>
        m.id === failedMessageId
          ? { ...m, imageSendStatus: "sending" as const, sentAt: formatLocalSentAt() }
          : m
      );
    });
    void this.uploadAndSendImage(failedMessageId, file);
  }

  getImageSendDebugLog(messageId: string): string | undefined {
    return this.imageSendDebugLogByMessageId.get(messageId);
  }

  clearImageSendFailureAlert() {
    this.imageSendFailureAlert = null;
  }

  private async uploadAndSendImage(localId: string, file: File) {
    const logs: string[] = [];
    const step = (line: string) => {
      const entry = `[${formatUploadStepTime()}] ${line}`;
      logs.push(entry);
      if (CS_IMAGE_UPLOAD_DEBUG) {
        console.log("[CS image]", entry);
      }
    };

    if (!this.conversationId) {
      step("失败: 会话未就绪（conversationId 为空）");
      this.markImageSendFailed(localId, file, logs.join("\n"));
      return;
    }

    step(
      `开始发送图片 origin=${typeof window !== "undefined" ? window.location.origin : "(unknown)"} name=${file.name || "(无)"} type=${file.type || "(空)"} size=${file.size}B conversationId=${this.conversationId}`
    );

    try {
      const { uploadFile, fileExt, compressed, compressNote } = await prepareCsImageUploadFile(file);
      step(
        `${compressNote} → upload name=${uploadFile.name} type=${uploadFile.type || "(空)"} size=${uploadFile.size}B ext=${fileExt} compressed=${compressed}`
      );

      step(`调用 prepareImageUpload(fileExt=${fileExt})…`);
      const prep = await SpaceCustomerChatApi.prepareImageUpload(this.conversationId, fileExt);
      if (typeof prep === "string") {
        step(`prepareImageUpload 失败: ${prep}`);
        this.markImageSendFailed(localId, file, logs.join("\n"));
        return;
      }
      const objectKey = prep.objectKey?.trim();
      if (!objectKey) {
        step("prepareImageUpload 返回空 objectKey");
        this.markImageSendFailed(localId, file, logs.join("\n"));
        return;
      }
      step(`prepareImageUpload 成功 objectKey=${objectKey}`);

      step("OSS 上传中…");
      let url: string;
      try {
        url = await uploadAppFileWithKey(uploadFile, objectKey);
      } catch (e) {
        step(`OSS 上传异常: ${e instanceof Error ? e.message : String(e)}`);
        throw e;
      }
      step(`OSS 上传成功 url=${url.length > 160 ? `${url.slice(0, 160)}…` : url}`);

      step("调用 sendMessage(IMAGE)…");
      const res = await SpaceCustomerChatApi.sendMessage(this.conversationId, url, "IMAGE");
      if (typeof res === "string") {
        step(`sendMessage 失败: ${res}`);
        this.markImageSendFailed(localId, file, logs.join("\n"));
        return;
      }
      step(`sendMessage 成功 messageId=${res.id}`);
      runInAction(() => {
        this.imageSendDebugLogByMessageId.delete(localId);
        this.applyImageSendSuccess(localId, normalizeMessage(res));
      });
      this.persistToLocal();
      void this.syncFromServer(true);
    } catch (e) {
      step(`未捕获异常: ${e instanceof Error ? e.message : String(e)}`);
      this.markImageSendFailed(localId, file, logs.join("\n"));
    }
  }

  private applyImageSendSuccess(localId: string, serverMessage: CsMessage) {
    const old = this.messages.find((m) => m.id === localId);
    this.revokeLocalPreview(old);
    this.imageRetryFileByMessageId.delete(localId);
    const withoutLocal = stripWelcome(this.messages).filter((m) => m.id !== localId);
    const merged = trimToVisibleWindow(mergeMessages(withoutLocal, [serverMessage]));
    this.messages = withWelcomeIfEmpty(merged);
    this.maxMessageId = resolveMaxMessageId(merged);
  }

  private markImageSendFailed(localId: string, file: File, debugLog: string) {
    runInAction(() => {
      this.imageRetryFileByMessageId.set(localId, file);
      if (CS_IMAGE_UPLOAD_DEBUG) {
        this.imageSendDebugLogByMessageId.set(localId, debugLog);
        this.imageSendFailureAlert = { messageId: localId, log: debugLog };
      }
      this.messages = this.messages.map((m) =>
        m.id === localId ? { ...m, imageSendStatus: "failed" as const } : m
      );
    });
  }

  private revokeLocalPreview(message?: CsMessage) {
    const url = message?.localPreviewUrl?.trim();
    if (!url) return;
    URL.revokeObjectURL(url);
  }

  async pollOnce() {
    await this.syncFromServer(true);
  }

  private async syncFromServer(markRead: boolean) {
    if (!this.conversationId || this.disposed) return;
    const readUpTo = markRead ? resolveReadUpToMessageId(this.messages) : undefined;
    const res = await SpaceCustomerChatApi.syncReadedAndGetUnread(
      this.conversationId,
      this.maxMessageId ?? undefined,
      readUpTo ?? undefined,
      this.maxRecallEventId ?? undefined
    );
    if (this.disposed || typeof res === "string") return;
    this.applySyncResult(res, Boolean(markRead));
  }

  private applySyncResult(res: CsSyncReadedAndGetUnreadResult, markRead: boolean) {
    if (this.disposed) return;
    runInAction(() => {
      if (res.conversationId) {
        this.conversationId = res.conversationId;
      }
      if (res.list?.length) {
        const merged = trimToVisibleWindow(
          mergeMessages(stripWelcome(this.messages), res.list.map(normalizeMessage))
        );
        this.messages = withWelcomeIfEmpty(merged);
        this.maxMessageId = resolveMaxMessageId(merged);
      }
      if (res.recalledMessageIds?.length) {
        this.messages = applyRecalled(this.messages, res.recalledMessageIds);
      }
      if (res.maxRecallEventId) {
        this.maxRecallEventId = res.maxRecallEventId;
      }
      if (res.hasOlder != null) {
        this.hasOlder = Boolean(res.hasOlder);
      }
      if (res.historyRecycled != null) {
        this.historyRecycled = Boolean(res.historyRecycled);
      }
      const unread = Math.max(0, Math.min(99, Number(res.unreadCount) || 0));
      if (markRead) {
        this.notifyUnreadChange(0);
      } else {
        this.notifyUnreadChange(unread);
      }
    });
    if (!this.disposed) {
      this.persistToLocal();
    }
  }

  private notifyUnreadChange(count: number) {
    if (this.disposed) return;
    this.onUnreadChange?.(count);
  }

  private persistToLocal() {
    if (!this.conversationId) return;
    const stored = stripWelcome(this.messages).filter((m) => !isLocalOutboundMessage(m));
    saveCsMessageCache(this.cacheScope, {
      conversationId: this.conversationId,
      messages: stored,
      maxMessageId: this.maxMessageId,
      minMessageId: stored[0]?.id ?? null,
      hasOlder: this.hasOlder,
      historyRecycled: this.historyRecycled,
    });
  }

  private startPoll() {
    this.stopPoll();
    this.pollTimer = setInterval(() => {
      void this.pollOnce();
    }, 3000);
  }

  private stopPoll() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}

function normalizeMessage(m: CsMessage): CsMessage {
  return { ...m, sentAt: formatApiDateTime(m.sentAt) ?? String(m.sentAt ?? "") };
}
